import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Link, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ConditionLifecycleSpine } from '../components/conditionLifecycle/ConditionLifecycleSpine';
import { VisualLifecycleExperience } from '../components/conditionLifecycle/VisualLifecycleExperience';
import {
  fetchSiteConditionState,
  type ConditionLifecycleSummary,
  type SiteConditionState,
} from '../domain/continuousSafety/siteConditionState';
import { isApiLifecycleHref } from '../domain/continuousSafety/conditionLifecycleNavigation';
import {
  buildVisualLifecycleModel,
  type VisualLifecycleDetail,
  type VisualZoneGeometry,
} from '../domain/continuousSafety/visualLifecycleModel';
import { useOverviewConditionEvidenceThumbs } from '../hooks/useOverviewConditionEvidenceThumbs';
import { useSiteTimeZone } from '../hooks/useSiteTimeZone';
import { api } from '../services/api';

export default function ConditionLifecyclePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const siteId = String(searchParams.get('site_id') || searchParams.get('site') || '').trim();
  const conditionId = String(searchParams.get('condition_id') || '').trim();
  const subjectKind = String(searchParams.get('subject_kind') || '').trim().toUpperCase();
  const compositeRuleId = String(searchParams.get('composite_rule_id') || '').trim();
  const compositeScopeType = String(searchParams.get('composite_scope_type') || 'camera').trim();
  const compositeScopeId = String(searchParams.get('composite_scope_id') || '').trim();
  const compositeRuleName = String(searchParams.get('composite_rule_name') || '').trim();
  const isCompositeSubject = subjectKind === 'COMPOSITE' && Boolean(compositeRuleId);
  const areaId = String(searchParams.get('area_id') || '').trim() || null;
  const zoneId = String(searchParams.get('zone_id') || '').trim() || null;
  const focus = String(searchParams.get('focus') || '').trim();
  const timeZone = useSiteTimeZone(siteId || null);
  const [condition, setCondition] = useState<SiteConditionState | null>(null);
  const [detail, setDetail] = useState<VisualLifecycleDetail | null>(null);
  const [zones, setZones] = useState<VisualZoneGeometry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [controlBusy, setControlBusy] = useState(false);

  useEffect(() => {
    if (!siteId || (!conditionId && !isCompositeSubject)) {
      setCondition(null);
      return;
    }
    if (isCompositeSubject) {
      setCondition(null);
      return;
    }
    let cancelled = false;
    void fetchSiteConditionState({ siteId, conditionIds: [conditionId] }).then((result) => {
      if (cancelled) return;
      if (result.status !== 'success') {
        setError(result.status === 'error' ? result.message : 'Condition lifecycle is unavailable.');
        setCondition(null);
        return;
      }
      setError(null);
      setCondition(result.data.conditions[0] || null);
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, conditionId, isCompositeSubject]);

  useEffect(() => {
    if (!siteId || (!conditionId && !isCompositeSubject)) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    const query = isCompositeSubject
      ? `subject_kind=COMPOSITE&composite_rule_id=${encodeURIComponent(compositeRuleId)}&composite_scope_type=${encodeURIComponent(compositeScopeType)}&composite_scope_id=${encodeURIComponent(compositeScopeId)}`
      : `condition_id=${encodeURIComponent(conditionId)}`;
    void api.get(`/api/v1/sites/${encodeURIComponent(siteId)}/condition-lifecycle?${query}`)
      .then((response) => {
        if (cancelled) return;
        const payload = (response.data || {}) as { conditions?: VisualLifecycleDetail[] };
        setDetail(payload.conditions?.[0] || null);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, conditionId, isCompositeSubject, compositeRuleId, compositeScopeType, compositeScopeId]);

  const compositeDetail = detail as VisualLifecycleDetail & {
    composite_control?: { control_revision_id?: string; control_objective?: string; implemented_at?: string } | null;
    composite_since?: { summary?: string; count?: number } | null;
    subject?: { composite_rule_name?: string; location_display?: string };
  } | null;
  const resolvedConditionId = conditionId || String((detail as { condition_id?: string } | null)?.condition_id || '');
  const cameraId = condition?.canonicalScope?.cameraIds[0] || (isCompositeSubject ? compositeScopeId : null);
  const compositeTitle = compositeRuleName
    || compositeDetail?.subject?.composite_rule_name
    || (isCompositeSubject ? 'Composite' : '');
  useEffect(() => {
    if (!siteId) {
      setZones([]);
      return;
    }
    let cancelled = false;
    void api.get('/api/v1/zones', {
      params: cameraId ? { camera_id: cameraId } : {},
    }).then((response) => {
      if (cancelled) return;
      const rows = Array.isArray(response.data)
        ? response.data
        : (response.data?.zones || response.data?.items || []);
      setZones(Array.isArray(rows) ? rows : []);
    }).catch(() => {
      if (!cancelled) setZones([]);
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, cameraId]);

  const evidenceRequests = useMemo(() => {
    if (!conditionId) return [];
    return [{
      conditionId,
      zoneId: zoneId || condition?.canonicalScope?.zoneIds[0] || null,
      cameraId,
    }];
  }, [conditionId, zoneId, cameraId, condition?.canonicalScope?.zoneIds]);
  const thumbs = useOverviewConditionEvidenceThumbs({
    siteId,
    requests: evidenceRequests,
    enabled: Boolean(siteId && conditionId),
  });
  const evidence = thumbs.byConditionId[conditionId];

  const lifecycle: ConditionLifecycleSummary | null = useMemo(() => (
    condition?.conditionLifecycle || (
      isCompositeSubject
        ? {
          latestImmediateIncident: null,
          latestTemporalPatternIncident: null,
          motivatingIncidentIds: [],
          controlRevisionId: compositeDetail?.composite_control?.control_revision_id || null,
          sinceStartedAt: compositeDetail?.composite_control?.implemented_at || null,
          latestScan: null,
          lifecycleHref: null,
          lifecycleUiPath: null,
          populationExplanation: compositeDetail?.composite_since?.summary || null,
          postControlPattern: null,
        }
        : null
    )
  ), [condition?.conditionLifecycle, isCompositeSubject, compositeDetail?.composite_control, compositeDetail?.composite_since?.summary]);
  const patternMembers = useMemo(() => {
    const incidents = detail?.incidents || [];
    return incidents.find((item) => String(item.trigger_kind || '') === 'TEMPORAL_PATTERN') || null;
  }, [detail]);
  const visualModel = useMemo(() => {
    if (!lifecycle) return null;
    return buildVisualLifecycleModel({
      lifecycle,
      condition,
      detail,
      route: {
        siteId,
        conditionId,
        areaId: areaId || condition?.canonicalScope?.areaId,
        zoneId: zoneId || condition?.canonicalScope?.zoneIds[0],
        cameraId,
        conditionFamily: condition?.conditionFamily,
      },
      zones,
      evidence: evidence || { status: 'idle', imageUrl: null, incidentId: null },
    });
  }, [lifecycle, condition, detail, siteId, conditionId, areaId, zoneId, cameraId, zones, evidence]);

  if (!siteId || (!conditionId && !isCompositeSubject)) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }} data-testid="condition-lifecycle-page">
        <Typography variant="h5">Condition lifecycle</Typography>
        <Typography color="text.secondary">site_id and condition_id are required.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, overflowX: 'hidden' }} data-testid="condition-lifecycle-page" data-focus={focus || undefined}>
      <Typography variant="overline" color="text.secondary">Condition lifecycle</Typography>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {isCompositeSubject ? (compositeTitle || 'Composite') : (condition?.conditionFamily || 'Linked condition')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Recurrence → Alarm → Control → Since → Verification. This page is the dashboard drill-down, not the lifecycle API.
      </Typography>
      {isCompositeSubject ? (
        <Box sx={{ mb: 2 }} data-testid="composite-lifecycle-control-panel">
          {compositeDetail?.composite_control ? (
            <Typography variant="body2" data-testid="composite-lifecycle-control-summary">
              {compositeDetail.composite_control.control_objective || 'Recorded control'}
              {compositeDetail.composite_since?.summary ? ` · ${compositeDetail.composite_since.summary}` : ''}
            </Typography>
          ) : (
            <Button
              variant="contained"
              size="small"
              disabled={controlBusy}
              data-testid="composite-lifecycle-record-control"
              onClick={() => {
                setControlBusy(true);
                void api.post(`/api/v1/sites/${encodeURIComponent(siteId)}/condition-lifecycle/controls`, {
                  subject_kind: 'COMPOSITE',
                  composite_rule_id: compositeRuleId,
                  composite_evaluated_scope_type: compositeScopeType,
                  composite_evaluated_scope_id: compositeScopeId,
                  composite_rule_version: 1,
                  composite_rule_name: compositeTitle,
                  control_objective: 'Separate pedestrian and forklift movement in Warehouse Room B',
                  implemented_at: new Date().toISOString(),
                  recorded_by: 'operator',
                  idempotency_key: `composite-control:${compositeRuleId}:${compositeScopeId}`,
                }).then(() => {
                  const query = `subject_kind=COMPOSITE&composite_rule_id=${encodeURIComponent(compositeRuleId)}&composite_scope_type=${encodeURIComponent(compositeScopeType)}&composite_scope_id=${encodeURIComponent(compositeScopeId)}`;
                  return api.get(`/api/v1/sites/${encodeURIComponent(siteId)}/condition-lifecycle?${query}`);
                }).then((response) => {
                  const payload = (response.data || {}) as { conditions?: VisualLifecycleDetail[] };
                  setDetail(payload.conditions?.[0] || null);
                }).finally(() => setControlBusy(false));
              }}
            >
              Record control
            </Button>
          )}
        </Box>
      ) : null}
      {error ? <Typography color="error">{error}</Typography> : null}
      {lifecycle ? (
        <ConditionLifecycleSpine
          lifecycle={lifecycle}
          siteId={siteId}
          conditionId={resolvedConditionId || conditionId}
          areaId={areaId || condition?.canonicalScope?.areaId}
          zoneId={zoneId || condition?.canonicalScope?.zoneIds[0]}
          cameraId={cameraId}
          conditionFamily={condition?.conditionFamily}
          timeZone={timeZone}
          density="page"
          onNavigate={(path) => {
            if (isApiLifecycleHref(path)) return;
            navigate(path);
          }}
          testId="condition-lifecycle-spine"
        />
      ) : (
        <Typography color="text.secondary">No linked condition lifecycle is available yet.</Typography>
      )}
      {visualModel ? (
        <VisualLifecycleExperience
          model={visualModel}
          timeZone={timeZone}
          density="page"
          focus={focus}
          onNavigate={(path) => {
            if (isApiLifecycleHref(path)) return;
            navigate(path);
          }}
        />
      ) : null}
      {focus === 'pattern-membership' ? (
        <Box sx={{ mt: 3 }} data-testid="lifecycle-pattern-membership">
          <Typography variant="subtitle2">Pattern Alert membership</Typography>
          {patternMembers ? (
            <>
              <Typography variant="body2">
                Observed {patternMembers.observed_count ?? 'unavailable'} against threshold
                {' '}{patternMembers.envelope_metadata?.min_events ?? '—'} in
                {' '}{patternMembers.envelope_metadata?.window_seconds ?? '—'}s.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Threshold occurrence {patternMembers.threshold_occurrence_id || 'not linked'}.
                Source occurrences {(patternMembers.source_occurrence_ids || []).join(', ') || 'not linked'}.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Pattern membership is not linked.</Typography>
          )}
        </Box>
      ) : null}
      <Box sx={{ mt: 3 }}>
        <Link
          href={`/acceptance/overview?site=${encodeURIComponent(siteId)}&condition_id=${encodeURIComponent(conditionId)}`}
          onClick={(event) => {
            event.preventDefault();
            navigate(`/acceptance/overview?site=${encodeURIComponent(siteId)}&condition_id=${encodeURIComponent(conditionId)}`);
          }}
          data-testid="lifecycle-back-to-om"
        >
          Back to Overview
        </Link>
      </Box>
    </Container>
  );
}
